import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import config from 'constants/config'
import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import Pane from 'components/Common/Pane'
import { take } from 'lib/arrayUtils'
import Image from 'components/Common/Image'
import { OFFER_TYPE_TOUR } from 'constants/offer'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import OfferTilePricing from './Support/OfferTilePricing'
import FrontPageTileFlightPricing from './Support/FrontPageTileFlightPricing'
import OfferTileLoggedInButtons from './Support/OfferTileLoggedInButtons'
import TextButton from 'components/Luxkit/Button/TextButton'
import FrontPageTileFlightOnlyPricing from './Support/FrontPageTileFlightOnlyPricing'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import OffsetBookmarkContainer from 'tripPlanner/components/Bookmark/Common/OffsetBookmarkContainer'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import GeoContext from 'contexts/geoContext'
import AspectRatio from 'components/utils/AspectRatio'
import { connect } from 'react-redux'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import pluralize from 'lib/pluralize'
import { LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import PrimaryButtonPaletteProvider, { PrimaryButtonPalette } from 'contexts/PrimaryButtonPaletteProvider'
import { showUserReviewsRating } from 'lib/reviewUtils'
import Group from 'components/utils/Group'
import { useTranslation } from 'react-i18next'

const Main = styled(Pane)`
  display: grid;
  position: relative;
  z-index: 0;
  padding: ${rem(20)};
  gap: ${rem(24)};

  ${mediaQueryUp.md} {
    grid-template-columns: ${rem(220)} 1fr ${rem(160)};
    grid-template-rows: 1fr;
  }
`

const DetailContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.md} {
    flex: 1;
  }
`

const DetailSegment = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const TopDetailSegment = styled.div`
  width: 100%;

  > * + * {
    margin-top: ${rem(12)};

    ${mediaQueryUp.md} {
      margin-top: ${rem(8)};
    }
  }
`

const OfferTitle = styled.div`
  display: grid;
  grid-template-columns: 1fr min-content;
  grid-row-gap: ${rem(4)};
  grid-column-gap: ${rem(16)};
  grid-auto-flow: column;

  ${mediaQueryUp.md} {
    grid-template-columns: 1fr;
    grid-auto-flow: row;
    grid-row-gap: ${rem(8)};
    justify-items: center;
  }
`

const MobileBookmarkContainer = styled.div`
  position: relative;
`

const LogoWrapper = styled.div`
  grid-column-start: 2;
  grid-row: span 2;
  align-self: center;
  position: absolute;
  right: 0;

  ${mediaQueryUp.md} {
    position: relative;
    grid-row: auto;
    grid-column-start: auto;
  }
`

const Logo = styled(Image)`
  width: ${rem(90)};
  height: ${rem(60)};

  ${mediaQueryUp.md} {
    width: ${rem(150)};
    height: ${rem(100)};
  }
`

const OfferContent = styled.div`
  > * + * {
    margin-top: ${rem(8)};

    ${mediaQueryUp.md} {
      margin-top: ${rem(12)};
    }
  }
`

const BottomDetailSegment = styled.div`
  display: grid;
  grid-template-columns: 1fr ${rem(120)};
  margin-top: ${rem(12)};
  justify-content: start;

  > * + * {
    margin-top: ${rem(16)};
  }

  ${mediaQueryUp.md} {
    grid-template-columns: 1fr;
  }
`

const UrgencyLabelsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  grid-column: 1 / 1;

  ${mediaQueryUp.md} {
    justify-content: center;
  }

  > * + * {
    margin-left: ${rem(4)};

    ${mediaQueryUp.md} {
      margin-left: ${rem(12)};
    }
  }
`

const MainImageSegment = styled.div`
  position: relative;
`

const OfferOverlayContainer = styled(LabelGroup)`
  position: absolute;
  top: ${rem(12)};
  left: ${rem(12)};
`

const StyledFrontPageTileFlightPricing = styled(FrontPageTileFlightPricing)`
  grid-column: 1 / -1;

  ${mediaQueryUp.md} {
    justify-content: center;
  }
`

const TileUrgencyLabels = styled(OfferUrgencyLabels)`
  ${mediaQueryUp.md} {
    justify-content: center;
  }
`

interface MappedProps {
  iOS: boolean;
}

interface Props {
  offer: App.Offer | App.OfferSummary;
  eagerLoadFirstImage?: boolean;
  offerUrl: string;
}

function CruiseV1SearchTile(props: Props & MappedProps) {
  const {
    offer,
    eagerLoadFirstImage,
    offerUrl,
    iOS,
  } = props

  const {
    property,
    offerFlightsEnabled,
    lowestPricePackage,
  } = offer

  const { t } = useTranslation(['common', 'hotelOffer', 'offerList'])
  const tour = lowestPricePackage?.tour

  const { currentRegionHasFlights } = useContext(GeoContext)

  const showBadge = !!offer.badge && config.BRAND === 'luxuryescapes'
  const departureDate = !!offer.travelToDate && moment(offer.travelToDate).format(LONG_MONTH_LONG_YEAR)

  const cruiseLocation = useMemo(() => {
    const isCruise = offer.holidayTypes.includes('Cruises')
    if (isCruise && offer.startLocation && offer.endLocation) {
      if (offer.startLocation === offer.endLocation) {
        return <BodyText variant="medium">Starts and ends in <strong>{offer.startLocation}</strong></BodyText>
      }

      return <BodyText variant="medium">Starts in <strong>{offer.startLocation}</strong>, Ends in <strong>{offer.endLocation}</strong></BodyText>
    }
    return offer.type === OFFER_TYPE_TOUR ? offer.location : [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')
  }, [offer])

  const pricingSection = useMemo(() => (<>
    {offer.bundledWithFlightsOnly && <FrontPageTileFlightOnlyPricing offer={offer} />}
    {!offer.bundledWithFlightsOnly && lowestPricePackage &&
      <OfferTilePricing
        offer={offer}
        pkg={lowestPricePackage}
        offerUrl={offerUrl}
      />}

    <TextButton kind="primary" fit="flex">{t('View offer', { ns: 'common' })}</TextButton>
    {currentRegionHasFlights && offerFlightsEnabled && !offer.bundledWithFlightsOnly &&
      <StyledFrontPageTileFlightPricing offer={offer}/>
    }
  </>), [offer, lowestPricePackage, offerUrl, offerFlightsEnabled, currentRegionHasFlights, t])

  // This is temporary until we can improve memory on the offer listing page
  const images = iOS ? take(offer.images, 1, 0) : offer.images
  const isUltraLux = offer.property?.isUltraLux || offer.productType === 'ultra_lux_tour'

  let paletteValue: PrimaryButtonPalette
  if (isUltraLux) {
    paletteValue = 'ultralux'
  } else {
    paletteValue = 'default'
  }

  const hasReview = showUserReviewsRating(offer.property?.rating, offer.property?.reviewsTotal)

  const departureInfo = useMemo(() => {
    const info: string[] = []
    if (offer.minDuration) {
      const nightLabel = pluralize({ singular: 'night', count: offer.minDuration }).text
      info.push(`${offer.minDuration} ${nightLabel}`)
    }

    if (offer.locations.length) {
      const portLabel = pluralize({ singular: 'port', count: offer.locations.length }).text
      info.push(`${offer.locations.length} ${portLabel}`)
    }

    return info.join(' • ').toLocaleLowerCase()
  }, [offer])

  return (
    <PrimaryButtonPaletteProvider palette={isUltraLux ? 'ultralux' : 'default'}>
      <Main type="clean">
        <MainImageSegment>
          <AspectRatio sm="9:12" xs="16:9" fillHeight>
            <ImageCarousel
              images={images}
              eagerLoadFirstImage={eagerLoadFirstImage}
            />
          </AspectRatio>
          <OfferOverlayContainer>
            <ProductTypeLabel productType={offer.productType} />
            <OfferPartnershipLabel offer={offer} />
          </OfferOverlayContainer>

          <MobileBookmarkContainer>
            <TripGuard>
              <CSSBreakpoint max="sm">
                <OffsetBookmarkContainer>
                  <BookmarkButton offer={offer} />
                </OffsetBookmarkContainer>
              </CSSBreakpoint>
            </TripGuard>
          </MobileBookmarkContainer>
        </MainImageSegment>

        <Group direction="vertical" gap={16}>
          {cruiseLocation}
          <Heading variant="heading4">{property?.name ?? offer.name}</Heading>

          <Group direction="vertical" gap={8}>
            {!!offer.vendorName &&
              <BodyText variant="medium" colour="neutral-two">
                Operated by: <strong>{offer.vendorName}</strong>
              </BodyText>
            }

            {!!offer.vendorVehicle &&
              <BodyText variant="medium" colour="neutral-two">
                Cruise ship: <strong>{offer.vendorVehicle}</strong>
              </BodyText>
            }

            {!!departureInfo && <BodyText variant="medium" colour="neutral-two">
              <strong>{departureInfo}</strong>
            </BodyText>}
          </Group>

          <BodyText variant="medium" weight="bold" colour="neutral-two">Sailing until {departureDate}</BodyText>
        </Group>

        <PrimaryButtonPaletteProvider palette={paletteValue}>
          <DetailContainer>
            <TripGuard>
              <CSSBreakpoint min="md">
                <Group direction={(!hasReview && !offer.bundledWithFlightsOnly) ? 'horizontal-reverse' : 'horizontal'} horizontalAlign="space-between">
                  {offer.property && hasReview && (
                    <NumberRating
                      variant="medium"
                      rating={offer.property.rating}
                      total={offer.property.reviewsTotal}
                      ratingSubtitle={offer.property.reviewsSource === 'google' ? t('From online reviews', { ns: 'common' }) : undefined}
                    />
                  )}
                  <BookmarkButton offer={offer} />
                </Group>
              </CSSBreakpoint>
            </TripGuard>
            <DetailSegment>
              <TopDetailSegment>
                <OfferTitle>
                  <LogoWrapper>
                    {property?.logoImageId && <Logo id={property.logoImageId} width="300" aspectRatio="3:2" alt={`${property.name || 'Vendor'} logo`}/>}
                    {tour?.logoImageId && <Logo id={tour.logoImageId} width="300" aspectRatio="3:2" alt={`${tour.name || 'Tour'} logo`}/>}
                  </LogoWrapper>
                  <TileUrgencyLabels offer={offer} />
                </OfferTitle>

                {offer.property && showUserReviewsRating(offer.property.rating, offer.property.reviewsTotal) && (
                  <CSSBreakpoint max="sm">
                    <NumberRating
                      inlineLabel
                      variant="xs"
                      rating={offer.property.rating}
                      total={offer.property.reviewsTotal}
                      ratingSubtitle={offer.property.reviewsSource === 'google' ? t('From online reviews', { ns: 'common' }) : undefined}
                    />
                  </CSSBreakpoint>
                )}

                <OfferContent>
                  {property && <BodyText variant="large">{offer.name}</BodyText>}

                  {(!!offer.badge || offer.bundledWithFlightsOnly) && <UrgencyLabelsWrapper>
                    {showBadge && <OfferBadgeTag badge={offer.badge} />}
                  </UrgencyLabelsWrapper>}
                </OfferContent>
              </TopDetailSegment>

              <BottomDetailSegment>
                {!offer.walledGarden && pricingSection}

                {offer.walledGarden && <OfferTileLoggedInButtons
                  signUpText={t('Sign up for free', { ns: 'common' })}
                  discountPercentage={Math.round(lowestPricePackage?.discountPercent ?? 0)}
                  align="none"
                  fullWidth
                >
                  {pricingSection}
                </OfferTileLoggedInButtons>}
              </BottomDetailSegment>
            </DetailSegment>
          </DetailContainer>
        </PrimaryButtonPaletteProvider>
      </Main>
    </PrimaryButtonPaletteProvider>
  )
}

const mapStateToProps = (state: App.State): MappedProps => ({
  iOS: state.config.userAgent.osName === 'iOS',
})

export default connect(mapStateToProps)(CruiseV1SearchTile)
